/**
 * Reusing `Intl` instances rather than constructing one per call.
 *
 * Constructing an `Intl` formatter is one to two orders of magnitude more expensive than using one:
 * measured on Node 24.11.1 / ICU 77.1, 13.594 µs against 0.215 µs for `Intl.NumberFormat`, and
 * 22.074 µs against 0.448 µs for `Intl.DateTimeFormat`. The naive implementation constructs one per
 * cell per render, which is fine for ten rows and visible jank for a few thousand. This module is
 * what makes raising a page size or switching on virtualization a decision nobody has to review for
 * localization cost.
 *
 * The instances themselves are immutable and stateless, so sharing one between unrelated callers is
 * safe: `format` reads the options it was built with and touches nothing else.
 */

/**
 * The cache is bounded rather than allowed to grow, and that is a deliberate trade.
 *
 * Options are caller data, so the key space is open — one caller passing a per-row
 * `maximumFractionDigits` would mint an entry per row forever. This package is a federation
 * singleton and lives as long as the tab does, so an unbounded map keyed on caller input is a leak.
 * At the bound, a pathological caller degrades to the uncached cost it would have paid anyway,
 * which is a performance regression rather than a growing heap. A hundred entries is far above the
 * dozen or so shapes an app actually formats, so eviction never runs in normal operation.
 */
export const INTL_CACHE_LIMIT = 100;

/**
 * `Intl.DurationFormat` reached stage 4 after the TypeScript lib this repo compiles against, so it
 * is described here rather than imported. Only the surface this package uses is declared, because a
 * fuller stub would be a second specification to keep correct for no gain.
 */
export type DurationFormatter = {
  format: (duration: {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
  }) => string;
};

type DurationFormatConstructor = new (
  locale: string,
  options?: { style?: 'long' | 'short' | 'narrow' | 'digital' }
) => DurationFormatter;

/**
 * The range half of `Intl.DateTimeFormat`, declared here for the reason above: this module holds
 * every `Intl` surface the package leans on that the ambient types do not describe.
 *
 * Optional rather than assumed, because an engine can carry `Intl.DateTimeFormat` without it and a
 * caller should read absence as a missing method rather than by probing a global.
 */
export type RangeFormatter = {
  readonly formatRangeToParts?: (
    start: Date,
    end: Date
  ) => readonly {
    readonly type: string;
    readonly source: string;
    readonly value: string;
  }[];
};

type CachedFormatter =
  | Intl.NumberFormat
  | Intl.DateTimeFormat
  | Intl.RelativeTimeFormat
  | Intl.ListFormat
  | DurationFormatter;

const cache = new Map<string, CachedFormatter>();

/**
 * Options serialised so that two equivalent objects produce one entry.
 *
 * Keys are sorted because `JSON.stringify` follows insertion order, which would otherwise make
 * `{ style, currency }` and `{ currency, style }` two entries for one formatter. Undefined values
 * are dropped for the same reason: omitting a key and passing it as `undefined` mean the same thing
 * to `Intl`, so they should mean the same thing here.
 */
const optionsKey = (options: object | undefined): string =>
  JSON.stringify(
    Object.entries(options ?? {})
      .filter(([, value]) => value !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : 1))
  );

// A null character cannot appear in a language tag, so no locale can collide with the separator.
const keyFor = (
  kind: string,
  locale: string,
  options: object | undefined
): string => `${kind}\u0000${locale}\u0000${optionsKey(options)}`;

const evictOldest = (): void => {
  for (const oldest of cache.keys()) {
    cache.delete(oldest);
    return;
  }
};

const cached = <T extends CachedFormatter>(key: string, build: () => T): T => {
  const hit = cache.get(key);

  if (hit) {
    // Re-inserting moves the entry to the end of the Map's insertion order, which is what makes the
    // first key the least recently *used* rather than merely the first created.
    cache.delete(key);
    cache.set(key, hit);
    // Safe because the key carries the formatter kind, so a `number` key can only ever hold what
    // `numberFormat` put there.
    return hit as T;
  }

  const built = build();

  if (cache.size >= INTL_CACHE_LIMIT) {
    evictOldest();
  }

  cache.set(key, built);
  return built;
};

/**
 * A `Intl.NumberFormat` for these options, constructed once.
 *
 * The locale must be one `Intl` accepts. Every caller inside this package passes a resolved
 * `locale`, which the preference resolver has already canonicalised or replaced, so a malformed tag
 * cannot reach here and this does not swallow the `RangeError` that would prove otherwise.
 */
export const numberFormat = (
  locale: string,
  options?: Intl.NumberFormatOptions
): Intl.NumberFormat =>
  cached(
    keyFor('number', locale, options),
    () => new Intl.NumberFormat(locale, options)
  );

/** An `Intl.DateTimeFormat` for these options, constructed once. See {@link numberFormat}. */
export const dateTimeFormat = (
  locale: string,
  options?: Intl.DateTimeFormatOptions
): Intl.DateTimeFormat =>
  cached(
    keyFor('dateTime', locale, options),
    () => new Intl.DateTimeFormat(locale, options)
  );

/** An `Intl.RelativeTimeFormat` for these options, constructed once. See {@link numberFormat}. */
export const relativeTimeFormat = (
  locale: string,
  options?: Intl.RelativeTimeFormatOptions
): Intl.RelativeTimeFormat =>
  cached(
    keyFor('relativeTime', locale, options),
    () => new Intl.RelativeTimeFormat(locale, options)
  );

/** An `Intl.ListFormat` for these options, constructed once. See {@link numberFormat}. */
export const listFormat = (
  locale: string,
  options?: Intl.ListFormatOptions
): Intl.ListFormat =>
  cached(
    keyFor('list', locale, options),
    () => new Intl.ListFormat(locale, options)
  );

/**
 * An `Intl.DurationFormat` for this locale, or `undefined` where the engine has none.
 *
 * The feature test lives here rather than at the call site so that every `Intl` construction in the
 * package goes through one module, and so a caller reads support as an absent formatter rather than
 * as a global it has to probe for itself.
 */
export const durationFormat = (
  locale: string,
  options?: { style?: 'long' | 'short' | 'narrow' | 'digital' }
): DurationFormatter | undefined => {
  const DurationFormat = (
    Intl as unknown as { DurationFormat?: DurationFormatConstructor }
  ).DurationFormat;

  if (DurationFormat === undefined) return undefined;

  return cached(
    keyFor('duration', locale, options),
    () => new DurationFormat(locale, options)
  );
};

/** Empties the cache. For tests that count constructions or exercise eviction. */
export const resetIntlCache = (): void => {
  cache.clear();
};

/** How many instances are currently held. For tests asserting the bound. */
export const intlCacheSize = (): number => cache.size;
